<template>
  <div :ref="listId" class="vue-view vue-view--team-actions-overview">
    <div class="vue-view__filters">
      <div class="filters__form">
        <div class="filters__filter">
          <label>{{ Drupal.t("Search for a team action", {}, {'context': 'VueJs Team Actions search'}) }}</label>

          <div class="d-flex flex-row align-items-center">
            <div class="filter-keyword-wrapper w-100 mr-1">
              <FormInput
                @change="onKeywordChange"
                :value="model.filter.keyword"
                :placeholder="Drupal.t('Search by keyword or location', {}, {'context': 'VueJs Team Actions search'})"
                class="form-control"
                :required="false"
                clearable
              />
              <button type="button" class="btn btn-primary" @click="applyFilters()">
                {{ Drupal.t("Zoek", {}, {'context': 'VueJs Team Actions search'}) }}
              </button>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-filters__toggle d-inline-flex align-items-center justify-content-center"
              @click="showAdvancedFilters = !showAdvancedFilters"
              aria-label="{{ Drupal.t('Advanced Filters', {}, {'context': 'VueJs Team Actions search'}) }}"
            >
              <i class="fa fa-sort-amount-asc m-0"></i>
              <span class="d-none d-lg-inline-block ml-lg-2">{{
                  Drupal.t("Filters", {}, {'context': 'VueJs Team Actions search'})
                }}</span>
            </button>
          </div>
        </div>
        <!-- Advanced Filters Dropdown -->
        <div v-show="showAdvancedFilters" class="advanced-filters bg-extralight mt-3 rounded p-4 position-relative">
          <button
            type="button"
            class="btn btn-sm advanced-filters__close position-absolute"
            @click="showAdvancedFilters = !showAdvancedFilters"
            aria-label="{{ Drupal.t('Advanced Filters', {}, {'context': 'VueJs Team Actions search'}) }}"
          >
            <i class="fa fa-close"></i>
            <span class="small">{{ Drupal.t("Sluiten", {}, {'context': 'VueJs Team Actions search'}) }}</span>
          </button>

          <!-- Categories Filter -->
          <div class="form-group">
            <MultiSelectFilter
              name="category-multiselect"
              :label="Drupal.t('Filter op categorieën', {}, {'context': 'VueJs Team Actions search'})"
              :value="model.filter.categories || []"
              :options="config.categories.options"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="true"
              :allow-empty="true"
              :placeholder="Drupal.t('- Selecteer categorieën -', {}, {'context': 'VueJs Team Actions search'})"
              @update="onCategoriesChange"
            />
          </div>

          <!-- Date Filter -->
          <label>{{ Drupal.t("Filter op datum", {}, {'context': 'VueJs Team Actions search'}) }}</label>
          <div class="form-group d-md-flex">
            <div class="col-md p-0">
              <Datepicker
                v-model="model.filter.dateStart"
                model-type="yyyy-MM-dd"
                locale="nl-BE"
                format="dd-MM-yyyy"
                placeholder="Startdatum"
                :enable-time-picker="false"
                :clearable="true"
                auto-apply
                teleport-center
                :transitions="false"
              />
            </div>

            <div class="col-md p-0 mt-2 mt-md-0 ml-md-2">
              <Datepicker
                v-model="model.filter.dateEnd"
                model-type="yyyy-MM-dd"
                locale="nl-BE"
                format="dd-MM-yyyy"
                placeholder="Einddatum"
                :enable-time-picker="false"
                :clearable="true"
                auto-apply
                teleport-center
                :transitions="false"
              />
            </div>
          </div>

          <!-- Location and Distance Filter -->
          <div class="form-group d-md-flex">
            <!-- Location Filter -->
            <div class="col-md p-0">
              <label>
                {{
                  Drupal.t(
                    "Postcode of gemeente",
                    {},
                    {context: "VueJs Teams search"}
                  )
                }}
              </label>
              <div class="filter-city-wrapper">
                <FormInput
                  @change="onCityChange"
                  @input="onCityChange"
                  :value="model.filter.city"
                  class="form-control"
                  :required="false"
                  :clearable="true"
                  autocomplete="off"
                />
                <div class="action-lookup-wrapper" v-if="searchCities.length">
                  <div class="action-lookup-results">
                    <ul class="list-angle">
                      <li
                        v-for="city in searchCities"
                        :key="city.postalcode"
                        @click="selectCity(city)"
                        class="action-lookup-result"
                      >
                        <a href="#" @click.prevent>{{ city.postalcode }} {{ city.city }}</a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Distance Filter -->
            <div class="col-md mt-2 mt-md-0 ml-md-2 p-0">
              <SelectFilter
                :list="this"
                name="distance"
                :label="Drupal.t('Afstand', {}, { context: 'VueJs Team Actions search' })"
                :value="model.filter.distance"
                :options="config.distances"
                @change="onDistanceChange"
              />
            </div>
          </div>

          <div v-if="filterError">
            <div class="alert alert-danger">{{ filterError }}</div>
          </div>

          <!-- Apply Filters Button -->
          <div class="text-left mt-3">
            <button
              type="button"
              class="btn btn-primary"
              @click="applyFilters"
            >
              {{ Drupal.t("Toepassen", {}, {'context': 'VueJs Team Actions search'}) }}
            </button>
            <button
              type="button"
              class="btn btn-link mx-2 btn-reset-filters"
              @click="resetFilters"
            >
              {{ Drupal.t("Alle filters wissen", {}, {'context': 'VueJs Team Actions search'}) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error">
      <div class="alert alert-danger">{{ error }}</div>
    </div>

    <Spinner v-if="loading"/>

    <div class="vue-view__content" :ref="listId + '__content'">
      <div v-if="loaded && !loading && !error">
        <div class="vue-view__results">
          <div v-if="!count">
            <div class="alert alert-info">
              {{ Drupal.t("No team actions were found.", {}, {'context': 'VueJs Team Actions search'}) }}
            </div>
          </div>

          <div v-else>
            <div class="row">
              <template v-for="row in results" :key="row">
                <slot :row="row">
                  {{ row }}
                </slot>
              </template>
            </div>

            <Pagination
              :count="count"
              :result-count="results.length"
              :totalPages="pagination.totalPages"
              :perPage="pagination.perPage"
              :currentPage="pagination.currentPage"
              @pagechanged="pagerClickCallback"
              v-if="pagination.totalPages > 1"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {parse} from "qs"
import {mapActions} from "vuex"
import {DrupalJsonApiParams} from "drupal-jsonapi-params"

import Pagination from "@/components/Pagination/PaginationComponent"
import Spinner from "@/components/General/SpinnerComponent"
import FormInput from "@/components/Form/FormInput.vue";
import SelectFilter from "@/components/Filters/SelectFilter.vue";
import MultiSelectFilter from "@/components/Filters/MultiSelectFilter.vue";
import cities from "@/data/cities.json";
import Datepicker from "@vuepic/vue-datepicker"

export default {
  name: "TeamActionsOverview",
  components: {
    Datepicker,
    SelectFilter,
    FormInput,
    Spinner,
    Pagination,
    MultiSelectFilter,
  },
  props: {
    actionId: null,
    perPage: null,
    categories: {
      type: String,
      required: false,
      default() {
        return ""
      }
    }
  },
  data() {
    // Parse our model from the visible query string.
    const queryString = this.$window.location.search.split("?")[1] || ""
    const model = parse(queryString)

    return {
      listId: "team_actions",
      path: this.$window.location.pathname,
      showAdvancedFilters: false,
      model: {
        filter: {
          keyword: model.filter?.keyword || "",
          categories: (model.filter?.categories || []).map(categoryId => parseInt(categoryId, 10)),
          dateStart: model.filter?.dateStart || "",
          dateEnd: model.filter?.dateEnd || "",
          city: model.filter?.city || "",
          distance: model.filter?.distance || "all",
        },
        page: parseInt(model.page) || 1,
        sort: model.sort || "field_date_end",
      },
      config: {
        pagination: {
          perPage: 10,
        },
        categories: {
          options: JSON.parse(this.categories)
        },
        distances: [
          {value: "all", label: "- Alle -"},
          {value: "10", label: "10 km"},
          {value: "20", label: "20 km"},
          {value: "50", label: "50 km"},
        ],
        isMobile: false
      },
      resource: null,
      Drupal: this.$drupal,
      loading: false,
      loaded: false,
      needsRefresh: false,
      error: false,
      filterError: null
    }
  },
  computed: {
    searchCities(e) {
      if (!this.model.filter?.city) {
        return []
      }

      if ((this.model.filter.city || '').length < 2) {
        return []
      }

      let matches = 0

      return cities.cities.filter(city => {
        if (city?.city.toLowerCase().includes(this.model.filter?.city.toLowerCase()) && matches < 10) {
          matches++
          return city
        }
        if (city?.postalcode.toString().startsWith(this.model.filter?.city.toLowerCase()) && matches < 10) {
          matches++
          return city
        }
      })
    },
    /**
     * Return the count of keys in our filter object.
     *
     * @returns {boolean}
     */
    hasActiveFilters() {
      let hasValues = false
      for (const filterKey in this.model.filter) {
        if (filterKey in this.model.filter) {
          if (filterKey === 'keyword') continue

          const filterValues = this.model.filter[filterKey];

          if ((Array.isArray(filterValues) || this.$helpers.isString(filterValues)) && filterValues.length) {
            hasValues = true
          }
        }
      }

      return hasValues
    },

    /**
     * The pagination.
     *
     * @returns {object}
     */
    pagination() {
      const pagination = {}

      pagination.perPage = this.perPage ?? this.config.pagination.perPage
      pagination.totalPages = this.count ? Math.ceil(this.count / pagination.perPage) : 0
      pagination.currentPage = this.model.page
      pagination.pageStartNum = ((pagination.currentPage - 1) * pagination.perPage) + 1
      pagination.pageEndNum = ((pagination.currentPage - 1) * pagination.perPage) + this.results.length

      return pagination
    },

    /**
     * The total item count.
     *
     * @type {int}
     */
    count() {
      return parseInt(((this.resource || {}).meta || {}).count) || 0
    },

    /**
     * Our resource results.
     *
     * @type {object[]}
     */
    results() {
      return (this.resource || {}).data || []
    },
  },
  methods: {
    onWindowResize(event) {
      this.config.isMobile = this.$helpers.isMobile()
    },

    /**
     * Scroll to the top of the application.
     */
    scrollToTop() {
      const element = this.$refs[this.listId + '__content']
      const top = element.offsetTop

      window.scrollTo(0, top);
    },

    /**
     * Builds the query for the JSON:API request.
     *
     * @param {object} settings - The merged module and component settings object.
     *
     * @return {DrupalJsonApiParams}
     */
    getQuery(settings = {}) {
      const query = new DrupalJsonApiParams()

      // Pagination.
      const pageParam = {}
      pageParam.limit = this.pagination.perPage

      if (this.model.page > 1) {
        pageParam.offset = (this.model.page - 1) * this.pagination.perPage
      }

      query.addCustomParam({
        page: pageParam
      })

      // Filters.
      if (this.model.filter.keyword) {
        query.addFilter("fulltext", this.model.filter.keyword)
      }

      // Categories filter
      if ((this.model.filter.categories || []).length > 0) {
        query.addFilter("field_categories", this.model.filter.categories, "IN")
      }

      // Date filter
      const now = new Date().toISOString().split(".")
      query.addFilter("field_date_end", now[0], ">=") // Ongoing actions

      if (this.model.filter.dateStart && this.model.filter.dateEnd && (this.model.filter.dateStart > this.model.filter.dateEnd)) {
        this.filterError = "De startdatum moet voor de einddatum liggen."
      } else {
        this.filterError = null
      }

      if (this.model.filter.dateStart) {
        query.addFilter("field_date_start", this.model.filter.dateStart, ">=")
      }
      if (this.model.filter.dateEnd) {
        const endDate = new Date(this.model.filter.dateEnd);
        endDate.setDate(endDate.getDate() + 1);
        query.addFilter("field_date_end", endDate.toISOString().split("T")[0], "<=")
      }

      // Location filter
      if (this.model.filter.city && this.model.filter.distance && this.model.filter.distance !== 'all') {
        query.addFilter("field_city", this.model.filter.city)
        query.addFilter("field_distance", this.model.filter.distance)
      }

      if (this.model.filter.city && (!this.model.filter.distance || (this.model.filter.distance === 'all'))) {
        // only get postalcode from city using regex
        let postalcode = null
        try {
          postalcode = this.model.filter.city.match(/\d+/)[0]
        } catch (e) {
          // do nothing.
        }

        if (postalcode) {
          query.addFilter("field_address_postalcode", postalcode)
        } else {
          query.addFilter("field_address_city", this.model.filter.city)
        }
      }

      // Exposed sorts.
      if (this.model.sort) {
        query.addSort(this.model.sort)
      }

      return query.getQueryObject()
    },

    /**
     * Wrapper for fetchData.
     *
     * @returns {Promise<*>}
     */
    async $fetch() {
      // Fetch resource.
      if (this.fetchData) {
        try {
          await this.fetchData.call(this, {})
        } catch (err) {
          // Build error details.
          const title = (err.response || {}).statusText || ((((err.response || {}).data || {}).errors || [])[0] || {}).title
          const summary = (err.response || {}).status
            ? [(err.response || {}).status, title].filter((s) => s).join(': ')
            : err.message

          console.log(title, summary)

          return false
        }
      }
    },

    /**
     * Filters update event handler.
     */
    onFiltersUpdate() {
      this.model.page = 1
    },

    /**
     * Filters update event handler.
     */
    onKeywordChange(value) {
      if (value.length) {
        this.model.filter.keyword = value
      } else {
        delete this.model.filter.keyword
      }
    },

    /**
     * Handle categories filter change.
     */
    onCategoriesChange(value) {
      this.model.filter = Object.assign({}, this.model.filter, {
        categories: value.map(value => parseInt(value, 10)),
      });
    },

    /**
     * Handle city filter change.
     *
     * @param {Object} value
     */
    onCityChange(value) {
      this.model.filter = Object.assign({}, this.model.filter, {
        city: value,
      });
    },

    /**
     * Handle distance filter change.
     *
     * @param {Object} value
     */
    onDistanceChange(value) {
      if (value === "") {
        value = "10";
      }

      this.model.filter = Object.assign({}, this.model.filter, {
        distance: value,
      });
    },

    selectCity(city) {
      this.model.filter = Object.assign({}, this.model.filter, {
        city: city.postalcode + ' ' + city.city,
      });
    },

    /**
     * Vuex actions.
     */
    ...mapActions("team_actions", [
      "getResults"
    ]),

    /**
     * Set the publicly visible query parameters.
     *
     * @param {object} query
     */
    setQueryParams(query) {
      if (query.sort && query.sort === "field_date_start") {
        delete query.sort
      }

      this.$router.push({path: this.path, query: query})
    },

    /**
     * Build the visible query parameters object.
     *
     * @returns {{filter, page, sort}}
     */
    buildQueryParams() {
      return this.model
    },

    /**
     * Build the visible query string from our model.
     */
    setModelToQueryString() {
      this.setQueryParams(this.buildQueryParams())
    },

    /**
     * Filter on the selected model values.
     */
    async applyFilters() {
      // Reset paging.
      this.onFiltersUpdate()

      this.setModelToQueryString()

      await this.$fetch()
    },

    /**
     * Build the visible query string from our model.
     */
    async resetFilters() {
      this.model = {
        filter: {},
        page: 1,
        sort: "-changed",
      }

      this.setModelToQueryString()

      await this.$fetch()
    },

    /**
     * Handle pager clicks.
     *
     * @param {int} pageNum
     */
    async pagerClickCallback(pageNum) {
      this.model.page = pageNum

      this.setModelToQueryString()

      await this.$fetch()

      this.scrollToTop()
    },

    /**
     * Fetch JSON:API results.
     *
     * @param {Object} settings
     */
    async fetchData(settings) {
      if (this.actionId) {
        const query = this.getQuery(settings)

        this.loading = true
        this.getResults({
          actionId: this.actionId,
          query: query,
        })
          .then(resource => {
            this.resource = resource

            this.loading = false
            this.loaded = true
          }).catch((error) => {
          this.error = Drupal.t("Could not load the team actions.", {}, {'context': 'VueJs Team Actions search'})
          this.loaded = false
        }).finally(() => {
          this.loading = false
        })
      }
    },
  },
  mounted() {
    this.$fetch()
  },
  created() {
    window.addEventListener("resize", this.onWindowResize)
    this.onWindowResize()
  },
  unmounted() {
    window.removeEventListener("resize", this.onWindowResize)
  }
}
</script>
